import { FilterMenu } from './../FilterMenu';
import { SearchCategorySelector } from '../SearchCategorySelector';
import { Grid, GridItem, GridRow } from './../Grid';
import { SortAndFilter } from './../SortAndFilter';
import { Locale, Markets, MediaLibraryItemType } from 'generated/graphql';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { MediaLibraryItemComponent } from './MediaLibraryItemComponent';
import { MediaLibraryModal } from './MediaLibraryModal';
import { getCopy } from '../../lib/utils/getCopy';
import { CorporateButton } from './../CorporateButton';
import getConfig from 'next/config';
import getSearchClient from '../../modules/searchClient';
import { algoliaPrefixes, facetsKeys } from '../../pages/api/webhook';
import { Hit, SearchResponse } from '@algolia/client-search';
import { CategoryWrapper, FilterCategoryWrapper, FilterWrapper } from './styles';

export const MediaLibrary = ({
  serverMediaLibraryData,
  locale,
  copy,
  currMarket,
}: {
  serverMediaLibraryData: any;
  locale: Locale;
  copy: any[];
  currMarket: Markets;
}) => {
  const router = useRouter();
  const { query } = router;
  const { id } = query;

  const initialFilterState = (() => {
    if (!serverMediaLibraryData?.facets) return [];

    const marketFacet = serverMediaLibraryData.facets.market || {};
    const categoryFacet = serverMediaLibraryData.facets.mediaCategory || {};
    // get markets

    const markets: any = Object.keys(marketFacet).map((market) => ({
      key: market,
      label: getCopy(`market_${market}`, copy),
      selected: market === currMarket ? true : false,
      itemCount: marketFacet[market],
    }));

    const categories: any = Object.keys(categoryFacet).map((category) => ({
      key: category,
      label: getCopy(`medialibrary_tag_${category}`, copy),
      selected: false,
      itemCount: categoryFacet[category],
    }));

    return [
      {
        key: 'category',
        label: getCopy('category', copy),
        options: categories,
        type: 'text',
      },
      {
        key: 'markets',
        label: getCopy('market', copy),
        options: markets,
        type: 'text',
      },
    ];
  })();

  const { publicRuntimeConfig } = getConfig();
  const searchClient = getSearchClient(
    publicRuntimeConfig.algoliaAppId,
    publicRuntimeConfig.algoliaApiKey,
  );
  const index = searchClient.initIndex(algoliaPrefixes['MediaLibrary']);
  const typeFilterValues: Record<string, string> = {
    all: 'All',
  };
  const typeFacets = serverMediaLibraryData?.facets?.mediaType || {};
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<MediaLibraryItemType | null>(null);
  const [filters, setFilters] = useState<any[]>(initialFilterState);
  const [hasLoaded, setLoaded] = useState<boolean>(false);
  const [mediaLibraryData, setMediaLibraryData] = useState<SearchResponse>(serverMediaLibraryData);
  const [searchFilters, setSearchFilters] = useState<string[]>();
  const [modalItem, setModalItem] = useState<Hit<any> | null>(null);
  const resultsPerPage: number = 12;
  const [perPageHits, setPerPageHits] = useState<number>(resultsPerPage);

  Object.keys(typeFacets).forEach((type) => {
    typeFilterValues[type] = getCopy(type, copy);
  });

  async function updateItems() {
    let filtersString = `locale:${locale}`;
    if (typeFilter) {
      filtersString += ` AND mediaType:${typeFilter}`;
    }
    if (filters.length > 0) {
      const categories = filters.filter((filter) => filter.key === 'category').pop();
      const markets = filters.filter((filter) => filter.key === 'markets').pop();
      const selectedCategories = categories.options
        .filter((category: { selected: boolean }) => category.selected)
        .map((category: { key: any }) => `mediaCategory:${category.key}`);
      const selectedMarkets = markets.options
        .filter((market: { selected: boolean }) => market.selected)
        .map((market: { key: any }) => `market:${market.key}`);

      if (selectedCategories.length > 0) {
        filtersString += ` AND (${selectedCategories.join(' OR ')})`;
      }
      if (selectedMarkets.length > 0) {
        filtersString += ` AND (${selectedMarkets.join(' OR ')})`;
      }
    }
    const indexSearch = await index.search('', {
      facets: facetsKeys['MediaLibrary'],
      hitsPerPage: perPageHits,
      filters: filtersString,
    });

    setMediaLibraryData(indexSearch);
  }

  const onItemClick = async (e: Event, itemId: string) => {
    e.preventDefault();
    // update media query
    router.push(
      {
        query: { id: itemId },
      },
      undefined,
      { shallow: true },
    );

    const item = mediaLibraryData.hits.find((item: any) => item.id === itemId);

    setModalItem(item);
  };

  const onModalClose = () => {
    router.push(
      {
        query: {},
      },
      undefined,
      { shallow: true },
    );

    setModalItem(null);
  };

  const loadMoreHandler = (event: any) => {
    event.preventDefault();
    if (perPageHits < mediaLibraryData.nbHits) {
      setPerPageHits(perPageHits + resultsPerPage);
    }
  };

  useEffect(() => {
    if (!hasLoaded) setLoaded(true);
  });

  useEffect(() => {
    if (hasLoaded) {
      updateItems();
    }
  }, [typeFilter, filters, perPageHits]);

  return (
    <>
      {modalItem && <MediaLibraryModal modalItem={modalItem} closeHandler={onModalClose} />}
      {hasLoaded && (
        <div className={`w-full h-full`}>
          <FilterMenu
            id="search-filter-menu"
            isOpen={showFilters}
            totalResults={mediaLibraryData.nbHits}
            title={'Filter'}
            onClose={() => setShowFilters(false)}
            onApplyFilters={(updatedFilters: any) => setFilters(updatedFilters)}
            filters={filters}
            labels={{
              showAll: getCopy('show_all', copy),
              showAllResults: getCopy('show_all_results', copy),
              showLess: getCopy('show_less', copy),
              applyFilters: getCopy('apply_filters', copy),
            }}
          />
        </div>
      )}
      <div className={`pt-5 relative`}>
        <FilterCategoryWrapper className="px-small pb-10 2xl:px-medium 3xl:px-xlarge w-full mt-10">
          <CategoryWrapper className="mb-4 md:mb-0 pb-2">
            <SearchCategorySelector
              selected={typeFilter === null ? 'all' : typeFilter}
              categories={typeFilterValues}
              onSelect={(val: any) => setTypeFilter(val !== 'all' ? val : null)}
            />
          </CategoryWrapper>
          <FilterWrapper>
            <SortAndFilter
              currentCount={mediaLibraryData.nbHits}
              currentFiltersCount={mediaLibraryData.hits.length}
              labels={{
                results: getCopy('results', copy),
                filters: getCopy('filters', copy),
              }}
              showFilter={true}
              onFilter={() => {
                setShowFilters(true);
              }}
            ></SortAndFilter>
          </FilterWrapper>
        </FilterCategoryWrapper>
      </div>

      <Grid>
        <GridRow columns={12}>
          {mediaLibraryData.hits.map((item: any) => (
            <GridItem colSpan={3} key={`media-item-${item.id}`} className="mb-8">
              <MediaLibraryItemComponent
                copy={copy}
                item={item}
                onItemClick={onItemClick}
                pathname={router.pathname}
              />
            </GridItem>
          ))}
        </GridRow>
        <GridRow columns={12}>
          {perPageHits < mediaLibraryData.nbHits && (
            <GridItem colSpan={12}>
              <div className="flex justify-center">
                <CorporateButton
                  buttonType="button"
                  type="button"
                  color="primaryalternative"
                  label={getCopy('load_more_button', copy)}
                  onClick={loadMoreHandler}
                />
              </div>
            </GridItem>
          )}
        </GridRow>
      </Grid>
    </>
  );
};
